/**
 * Shared scoring visibility: always include base rows; add expansion rows
 * only for active selections. No duplicate categories / empty buckets.
 */

import type { ContentBlock } from '../types';
import type { GamePrefs } from '../state/gamePrefs';
import { withDefaultSelection } from './selectableContent';
import { filterArkNovaScoring } from './ark-nova/scoringTables';
import { filterSpiritIslandScoring } from './spirit-island/scoringTables';
import {
  filterScoringRulesForModules,
  buildScoringTable,
} from '../games/carcassonne/data/scoring-rules';
import type { CarcassonneModuleId } from '../games/carcassonne/types';
import { filterVinhosScoring } from '../games/vinhos-deluxe/data/scoringTables';
import type { GameConfiguration } from '../games/vinhos-deluxe/types';
import { filterOdinScoring } from './a-feast-for-odin/scoringTables';
import { filterProjektGajaScoring } from './projekt-gaja/scoringTables';
import { filterVoidfallScoring } from './voidfall/scoringTables';
import { filterBarrageScoring } from './barrage/scoringTables';
import { onMarsProgressScoring } from './on-mars/scoringTables';
import { MARINE_WORLDS_EXPANSION_ID } from './ark-nova/marine-worlds/expansion';

export type ScoringRow = {
  id: string;
  category?: string;
  element: string;
  scoring: string;
  conditions?: string;
  moduleId?: string;
};

const VINHOS_EXPANSION_TO_FLAG: Record<
  string,
  keyof Pick<
    GameConfiguration,
    'experts' | 'islands' | 'tastingRoom' | 'connoisseur'
  >
> = {
  'vinhos-experts': 'experts',
  'vinhos-islands': 'islands',
  'vinhos-tasting-room': 'tastingRoom',
  'vinhos-connoisseur': 'connoisseur',
  experts: 'experts',
  islands: 'islands',
  tastingRoom: 'tastingRoom',
  connoisseur: 'connoisseur',
};

function uniqueById(rows: ScoringRow[]): ScoringRow[] {
  const seen = new Set<string>();
  const out: ScoringRow[] = [];
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    if (!row.element.trim() && !row.scoring.trim()) continue;
    out.push(row);
  }
  return out;
}

function vinhosConfigFromPrefs(prefs: GamePrefs): GameConfiguration {
  const flags = {
    experts: false,
    islands: false,
    tastingRoom: false,
    connoisseur: false,
  };
  for (const id of [
    ...prefs.selectedExpansionIds,
    ...prefs.selectedModuleIds,
  ]) {
    const key = VINHOS_EXPANSION_TO_FLAG[id];
    if (key) flags[key] = true;
  }
  const activeVariantId =
    prefs.activeVariantId === 'reserve-2010'
      ? 'reserve-2010'
      : 'special-vintage-2016';
  return {
    schemaVersion: 2,
    playerCount: 2,
    activeVariantId,
    specialVintage2016: activeVariantId === 'special-vintage-2016',
    vintage2010: activeVariantId === 'reserve-2010',
    ...flags,
  };
}

/** Module ids understood by each game's scoring filter (always includes base). */
export function resolveActiveScoringModuleIds(
  gameId: string,
  prefsInput: GamePrefs
): string[] {
  const prefs = withDefaultSelection(gameId, prefsInput);
  const selected = new Set([
    ...prefs.selectedExpansionIds,
    ...prefs.selectedModuleIds,
  ]);

  switch (gameId) {
    case 'ark-nova': {
      const ids = new Set<string>(['base']);
      if (
        selected.has(MARINE_WORLDS_EXPANSION_ID) ||
        selected.has('marine-worlds')
      ) {
        ids.add('marine-worlds');
      }
      return Array.from(ids);
    }
    case 'spirit-island': {
      const ids = new Set<string>(['base']);
      for (const id of [
        'branch-and-claw',
        'jagged-earth',
        'feather-and-flame',
      ]) {
        if (selected.has(id)) ids.add(id);
      }
      return Array.from(ids);
    }
    case 'carcassonne':
      return prefs.selectedModuleIds.length > 0
        ? prefs.selectedModuleIds
        : ['base'];
    case 'uczta-dla-odyna': {
      const ids = new Set<string>(['base']);
      if (selected.has('norwegians')) ids.add('norwegians');
      return Array.from(ids);
    }
    case 'projekt-gaja': {
      const ids = new Set<string>(['base']);
      if (selected.has('lost-fleet')) ids.add('lost-fleet');
      return Array.from(ids);
    }
    case 'vinhos-deluxe': {
      const ids = new Set<string>(['base']);
      const config = vinhosConfigFromPrefs(prefs);
      if (config.experts) ids.add('experts');
      if (config.islands) ids.add('islands');
      if (config.tastingRoom) ids.add('tastingRoom');
      if (config.connoisseur) ids.add('connoisseur');
      return Array.from(ids);
    }
    default:
      return ['base'];
  }
}

export function getScoringRowsForSelection(
  gameId: string,
  prefsInput: GamePrefs
): ScoringRow[] {
  const prefs = withDefaultSelection(gameId, prefsInput);
  const modules = resolveActiveScoringModuleIds(gameId, prefs);

  switch (gameId) {
    case 'ark-nova':
      return uniqueById(
        filterArkNovaScoring(
          modules as Array<'base' | 'marine-worlds'>
        ).map((row) => ({
          id: row.id,
          category: row.category,
          element: row.element,
          scoring: row.scoring,
          conditions: row.conditions,
          moduleId: row.moduleId,
        }))
      );
    case 'spirit-island':
      return uniqueById(
        filterSpiritIslandScoring(
          modules as Array<
            | 'base'
            | 'branch-and-claw'
            | 'jagged-earth'
            | 'feather-and-flame'
          >
        ).map((row) => ({
          id: row.id,
          category: row.category,
          element: row.element,
          scoring: row.scoring,
          conditions: row.conditions,
          moduleId: row.moduleId,
        }))
      );
    case 'carcassonne': {
      const moduleIds = modules as CarcassonneModuleId[];
      void filterScoringRulesForModules(moduleIds);
      return uniqueById(
        buildScoringTable(moduleIds).map((row) => ({
          id: row.id,
          category: row.category,
          element: row.element,
          scoring: row.scoring,
          conditions: row.conditionsAndExceptions,
          moduleId: row.moduleId,
        }))
      );
    }
    case 'vinhos-deluxe':
      return uniqueById(
        filterVinhosScoring(vinhosConfigFromPrefs(prefs)).map((row) => ({
          id: row.id,
          category: row.category,
          element: row.element,
          scoring: row.scoring,
          conditions: row.conditions,
          moduleId: row.moduleId,
        }))
      );
    case 'uczta-dla-odyna':
      return uniqueById(
        filterOdinScoring(
          modules as Array<'base' | 'norwegians' | 'solo'>
        ).map((row) => ({
          id: row.id,
          category: row.category,
          element: row.element,
          scoring: row.scoring,
          conditions: row.conditions,
          moduleId: row.moduleId,
        }))
      );
    case 'projekt-gaja':
      return uniqueById(
        filterProjektGajaScoring(
          modules as Array<'base' | 'lost-fleet'>
        ).map((row) => ({
          id: row.id,
          category: row.category,
          element: row.element,
          scoring: row.scoring,
          conditions: row.conditions,
          moduleId: row.moduleId,
        }))
      );
    case 'voidfall':
      return uniqueById(
        filterVoidfallScoring(['base']).map((row) => ({
          id: row.id,
          category: row.category,
          element: row.element,
          scoring: row.scoring,
          conditions: row.conditions,
          moduleId: row.moduleId,
        }))
      );
    case 'barrage':
      return uniqueById(
        filterBarrageScoring().map((row) => ({
          id: row.id,
          category: row.category,
          element: row.element,
          scoring: row.scoring,
          conditions: row.conditions,
          moduleId: 'base',
        }))
      );
    case 'on-mars':
      return uniqueById(
        onMarsProgressScoring.map((row) => ({
          id: row.id,
          category: 'postęp',
          element: row.label,
          scoring: `${row.points} OP`,
          conditions: row.notes,
          moduleId: 'base',
        }))
      );
    default:
      return [];
  }
}

export function scoringRowsToBlocks(rows: ScoringRow[]): ContentBlock[] {
  if (rows.length === 0) {
    return [
      {
        type: 'paragraph',
        text: 'Brak tabel punktacji dla aktywnej konfiguracji tej gry.',
      },
    ];
  }

  const blocks: ContentBlock[] = [
    { type: 'heading', text: 'Tabele punktacji', level: 2 },
  ];
  let lastCategory: string | undefined;
  for (const row of rows) {
    if (row.category && row.category !== lastCategory && row.category !== row.element) {
      blocks.push({
        type: 'heading',
        text: row.category,
        level: 3,
      });
      lastCategory = row.category;
    }
    blocks.push({ type: 'heading', text: row.element, level: 3 });
    blocks.push({ type: 'paragraph', text: row.scoring });
    if (row.conditions && row.conditions !== '—') {
      blocks.push({ type: 'tip', text: `Warunki: ${row.conditions}` });
    }
  }
  return blocks;
}

/** Filter manual chapters by requiredAddons vs selected expansion/addon ids. */
export function filterChaptersByAddons<
  T extends { requiredAddons?: string[] }
>(chapters: T[], prefs: GamePrefs): T[] {
  const active = new Set([
    'base',
    ...prefs.selectedExpansionIds,
    ...prefs.selectedModuleIds,
  ]);
  // Marine Worlds catalog id ↔ short scoring id
  if (active.has(MARINE_WORLDS_EXPANSION_ID)) {
    active.add('marine-worlds');
    active.add(MARINE_WORLDS_EXPANSION_ID);
  }
  return chapters.filter(
    (chapter) =>
      !chapter.requiredAddons ||
      chapter.requiredAddons.some((addon) => active.has(addon))
  );
}
